// src/App.js - Clean version without client registration
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import LandingPage from './components/LandingPage';
import AdminPortal from './components/AdminPortal';
import AdminLogin from './components/AdminLogin';
import TrainerPortal from './components/TrainerPortal';
import TrainerSelection from './components/TrainerSelection';
import LoginPage from './components/LoginPage';
import ResetPassword from './components/ResetPassword';
import ClientPortal from './components/ClientPortal';
import TrainerSetup from './components/TrainerSetup';
import PaymentSuccess from './components/PaymentSuccess';
import TrainerPaymentSuccess from './components/TrainerPaymentSuccess';
import VideoCallPage from './components/VideoCallPage';
import PostSessionForm from './components/PostSessionForm';
import apiService from './services/api';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState('landing');
  const [isTrainerLoggedIn, setIsTrainerLoggedIn] = useState(false);
  const [isClientLoggedIn, setIsClientLoggedIn] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isTrainerSetup, setIsTrainerSetup] = useState(false);
  const [isDevelopmentMode] = useState(true);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [sessionOptions, setSessionOptions] = useState(null);
  const [trainerLoginData, setTrainerLoginData] = useState({
    email: '',
    password: ''
  });
  const [verificationData, setVerificationData] = useState({
    email: '',
    captchaVerified: false,
    agreeToTerms: false
  });

  // Session persistence - check localStorage on component mount
  useEffect(() => {
    console.log('=== APP COMPONENT MOUNTED - CHECKING SESSION PERSISTENCE ===');
    console.log('localStorage contents:', localStorage);
    
    // Check admin login status separately (only affects /admin routes)
    if (localStorage.getItem('adminLoggedIn') === 'true') {
      setIsAdminLoggedIn(true);
    }
    
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const trainerId = localStorage.getItem('trainerId');
    const clientId = localStorage.getItem('clientId');
    
    console.log('Session data found:', { token, username, trainerId, clientId });
    
    // If we have a token and user data, restore the session (only for client/trainer, not admin)
    if (token && username) {
      console.log('Valid session found, restoring user state...');
      
      if (trainerId && !clientId) {
        // User is a trainer
        console.log('Restoring trainer session');
        setIsTrainerLoggedIn(true);
        setCurrentPage('trainer');
      } else if (clientId && !trainerId) {
        // User is a client
        console.log('Restoring client session');
        setIsClientLoggedIn(true);
        setCurrentPage('client-portal');
      } else if (trainerId && clientId) {
        // This shouldn't happen, but if it does, prioritize trainer
        console.log('Both trainer and client IDs found, prioritizing trainer');
        setIsTrainerLoggedIn(true);
        setCurrentPage('trainer');
      } else {
        // User is logged in but no specific portal ID - check if they need setup
        console.log('User logged in but no portal ID found, checking profile completion...');
        // Check if user needs to complete trainer setup
        checkUserSetupStatus(username, token);
      }
    } else {
      console.log('No valid session found, staying on landing page');
    }
  }, []); // Empty dependency array means this runs once on mount

  // Handle browser close/refresh - mark trainer as inactive if they're logged in
  useEffect(() => {
    const handleBeforeUnload = async () => {
      const trainerId = localStorage.getItem('trainerId');
      if (trainerId) {
        console.log('=== BROWSER CLOSING/REFRESHING - MARKING TRAINER INACTIVE ===');
        try {
          // Use sendBeacon for more reliable delivery when page is unloading
          const data = new URLSearchParams();
          data.append('trainer_id', trainerId);
          
          // Use sendBeacon if available, otherwise fallback to fetch
          if (navigator.sendBeacon) {
            navigator.sendBeacon(
              'http://localhost:8000/session/makeTrainerInactive?' + data.toString()
            );
            console.log('✅ Trainer marked as inactive via sendBeacon');
          } else {
            // Fallback to fetch (may not complete if page unloads too quickly)
            fetch('http://localhost:8000/session/makeTrainerInactive?' + data.toString(), {
              method: 'POST',
              keepalive: true
            }).then(response => {
              console.log('✅ Trainer marked as inactive via fetch fallback');
            }).catch(error => {
              console.warn('⚠️ Failed to mark trainer as inactive on unload:', error);
            });
          }
        } catch (error) {
          console.error('❌ Error marking trainer as inactive on unload:', error);
        }
      }
    };

    // Add event listener for beforeunload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Check if user needs to complete setup (for trainers)
  const checkUserSetupStatus = async (username, token) => {
    try {
      console.log('Checking setup status for user:', username);
      
      // Try to get trainer ID to see if profile is completed
      const response = await apiService.getTrainerID(username);
      console.log('Trainer ID check response:', response);
      
      if (response.Code === 200 && response.id) {
        // Trainer profile exists, store ID and go to trainer portal
        localStorage.setItem('trainerId', response.id);
        setIsTrainerLoggedIn(true);
        setCurrentPage('trainer');
        console.log('Trainer profile found, redirecting to trainer portal');
      } else {
        // No trainer profile found, might need setup
        console.log('No trainer profile found, checking if user needs setup');
        setCurrentPage('landing');
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
      setCurrentPage('landing');
    }
  };

  const handleCaptchaVerification = () => {
    const randomMath = Math.floor(Math.random() * 10) + 1;
    const randomMath2 = Math.floor(Math.random() * 10) + 1;
    const answer = prompt(`Human Verification: What is ${randomMath} + ${randomMath2}?`);
    
    if (parseInt(answer) === randomMath + randomMath2) {
      setVerificationData({...verificationData, captchaVerified: true});
      alert('✅ Human verification complete!');
    } else {
      alert('❌ Incorrect answer. Please try again.');
    }
  };

  const handleTrainerLogin = (e) => {
    e.preventDefault();
    console.log('Trainer login attempt:', trainerLoginData);
    
    if (trainerLoginData.email && trainerLoginData.password) {
      setIsTrainerLoggedIn(true);
      setCurrentPage('trainer');
      alert('Login successful! Welcome back to your trainer portal.');
    } else {
      alert('Please enter both email and password.');
    }
  };

  const handleTrainerPortalClick = () => {
    if (isDevelopmentMode) {
      setIsTrainerLoggedIn(true);
      setCurrentPage('trainer');
      console.log('Development mode: Skipping login, going directly to trainer portal');
    } else if (isTrainerLoggedIn) {
      setCurrentPage('trainer');
    } else {
      setCurrentPage('login');
    }
  };

  const handleLogout = async () => {
    console.log('=== LOGOUT INITIATED ===');
    
    // Check if user is a trainer and mark them as inactive
    const trainerId = localStorage.getItem('trainerId');
    if (trainerId) {
      console.log('User is a trainer, marking as inactive before logout');
      try {
        const response = await apiService.makeTrainerInactive(trainerId);
        console.log('Make trainer inactive response:', response);
        if (response.Code === 200) {
          console.log('✅ Trainer successfully marked as inactive');
        } else {
          console.warn('⚠️ Failed to mark trainer as inactive:', response.Message);
        }
      } catch (error) {
        console.error('❌ Error marking trainer as inactive:', error);
        // Continue with logout even if this fails
      }
    }
    
    // Clear all state
    setIsTrainerLoggedIn(false);
    setIsClientLoggedIn(false);
    setIsTrainerSetup(false);
    setTrainerLoginData({ email: '', password: '' });
    setVerificationData({ email: '', captchaVerified: false, agreeToTerms: false });
    setCurrentPage('landing');
    
    // Clear any stored authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('trainerId');
    localStorage.removeItem('clientId');
    
    console.log('=== LOGOUT COMPLETE ===');
    
    // Navigate to landing page
    navigate('/');
  };

  const handleTrainerVerification = (e) => {
    e.preventDefault();
    
    if (!verificationData.email.trim()) {
      alert('Please enter your email address.');
      return;
    }
    
    if (!verificationData.captchaVerified) {
      alert('Please complete the human verification.');
      return;
    }
    
    if (!verificationData.agreeToTerms) {
      alert('Please agree to the terms and conditions.');
      return;
    }
    
    alert('✅ Verification request submitted! We\'ll review your credentials and get back to you within 24 hours.');
    setCurrentPage('landing');
  };

  const handleNavigateToClient = () => {
    navigate('/login');
  };

  const handleNavigateToTrainer = () => {
    navigate('/login');
  };

  const handleNavigateToAdmin = () => {
    navigate('/admin-login');
  };

  const handleSelectTrainer = (trainer, options) => {
    setSelectedTrainer(trainer);
    setSessionOptions(options);
    navigate('/session');
  };

  const handleBackToClientPortal = () => {
    navigate('/client-portal');
  };
  
  const handleLoginSuccess = (userType) => {
    console.log('=== HANDLE LOGIN SUCCESS CALLED ===');
    console.log('User type:', userType);
    
    if (userType === 'trainer') {
      console.log('Redirecting to trainer portal...');
      setIsTrainerLoggedIn(true);
      navigate('/trainer');
    } else if (userType === 'client') {
      console.log('Redirecting to client portal...');
      setIsClientLoggedIn(true);
      navigate('/client-portal');
    } else if (userType === 'trainer-setup') {
      console.log('Redirecting to trainer setup...');
      setIsTrainerLoggedIn(true);
      navigate('/trainer-setup');
    } else {
      console.log('Redirecting to selection...');
      navigate('/selection');
    }
  };

  const handleBackToHome = () => {
    console.log('=== HANDLING BACK TO HOME ===');
    navigate('/');
    setIsTrainerLoggedIn(false);
    setIsClientLoggedIn(false);
    setIsAdminLoggedIn(false);
    setIsTrainerSetup(false);
    setSelectedTrainer(null);
    setSessionOptions(null);
    setTrainerLoginData({ email: '', password: '' });
    setVerificationData({ email: '', captchaVerified: false, agreeToTerms: false });
    localStorage.clear();
    console.log('Cleared all state and localStorage, returning to landing page');
  };

  // Trainer Verification Page
  if (currentPage === 'trainer-verification') {
    return (
      <div style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          maxWidth: '500px',
          width: '100%'
        }}>
          <button
            onClick={() => setCurrentPage('landing')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#7f8c8d',
              fontSize: '1rem',
              cursor: 'pointer',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back to Home
          </button>

          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(45deg, #e67e22, #f39c12)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.5rem',
              margin: '0 auto 15px auto'
            }}>
              <img 
                src="/images/logo.png" 
                alt="Pheefee Logo" 
                style={{width: '60px', height: '60px', objectFit: 'contain'}}
              />
            </div>
            <h1 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              color: '#2c3e50',
              marginBottom: '10px'
            }}>
              Trainer Verification
            </h1>
            <p style={{
              color: '#7f8c8d',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              Submit your credentials for verification
            </p>
          </div>

          <form onSubmit={handleTrainerVerification}>
            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '8px'
              }}>
                Professional Email *
              </label>
              <input
                type="email"
                value={verificationData.email}
                onChange={(e) => setVerificationData({...verificationData, email: e.target.value})}
                placeholder="your.email@professional.com"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <button
                type="button"
                onClick={handleCaptchaVerification}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  backgroundColor: verificationData.captchaVerified ? '#d4edda' : '#f8f9fa',
                  color: verificationData.captchaVerified ? '#155724' : '#6c757d',
                  cursor: 'pointer'
                }}
              >
                {verificationData.captchaVerified ? '✅ Human Verified' : '🤖 Complete Human Verification'}
              </button>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={verificationData.agreeToTerms}
                  onChange={(e) => setVerificationData({...verificationData, agreeToTerms: e.target.checked})}
                  style={{ transform: 'scale(1.2)' }}
                />
                <span style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
                  I agree to the Terms of Service and Privacy Policy
                </span>
              </label>
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                background: 'linear-gradient(45deg, #e67e22, #f39c12)',
                color: 'white',
                border: 'none',
                padding: '15px',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
            >
              Submit for Verification
            </button>
          </form>
        </div>
      </div>
    );
  }


  return (
    <div className="App">
      <Routes>
        <Route path="/" element={
        <LandingPage 
          onNavigateToClient={handleNavigateToClient}
          onNavigateToTrainer={handleNavigateToTrainer}
          onNavigateToAdmin={handleNavigateToAdmin}
        />
        } />
        
        <Route path="/admin-login" element={
          <AdminLogin 
            onLoginSuccess={() => {
              setIsAdminLoggedIn(true);
              navigate('/admin');
            }}
            onBackToHome={handleBackToHome}
          />
        } />
        
        <Route path="/admin" element={
          (isAdminLoggedIn || localStorage.getItem('adminLoggedIn') === 'true') ? (
            <AdminPortal 
              onBackToHome={handleBackToHome}
              onLogout={() => {
                localStorage.removeItem('adminLoggedIn');
                setIsAdminLoggedIn(false);
                handleLogout();
                navigate('/admin-login');
              }}
            />
          ) : (
            <Navigate to="/admin-login" replace />
          )
        } />
        
        <Route path="/trainer-selection" element={
        <TrainerSelection 
          onBackToClientPortal={handleBackToClientPortal}
          onSelectTrainer={handleSelectTrainer}
        />
        } />
        
        <Route path="/session" element={
        <div style={{
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: '600px',
            width: '100%'
          }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '20px', color: '#f39c12' }}>
              Session Starting Soon
            </h1>
            <p style={{ fontSize: '1.2rem', marginBottom: '30px' }}>
              Your session with {selectedTrainer?.name} is being prepared.
            </p>
            <div style={{
              background: 'rgba(46, 204, 113, 0.1)',
              border: '1px solid #2ecc71',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '30px'
            }}>
              <h3 style={{ color: '#2ecc71', marginBottom: '10px' }}>Session Details</h3>
              <p>Duration: {sessionOptions?.sessionDuration} minutes</p>
              <p>Cost: ${selectedTrainer ? (selectedTrainer.first10MinRate + ((sessionOptions?.sessionDuration - 10) * selectedTrainer.perMinuteRate)).toFixed(2) : '0.00'}</p>
              {sessionOptions?.reviewPreviousSessions && (
                <p style={{ color: '#f39c12' }}>✓ Previous sessions will be reviewed</p>
              )}
            </div>
            <button
              onClick={handleBackToClientPortal}
              style={{
                background: 'linear-gradient(45deg, #e67e22, #f39c12)',
                color: 'white',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Back to Client Portal
            </button>
          </div>
        </div>
        } />
        
        <Route path="/login" element={
          <LoginPage onBackToHome={handleBackToHome} onLoginSuccess={handleLoginSuccess} />
        } />
        
        <Route path="/reset-password" element={
          <ResetPassword onBackToHome={handleBackToHome} />
        } />
        
        <Route path="/client-portal" element={
          <ClientPortal 
            onBackToHome={() => navigate('/')}
            onLogout={handleLogout}
            isLoggedIn={isClientLoggedIn}
            onFindTrainer={() => navigate('/trainer-selection')}
          />
        } />
        
        <Route path="/trainer" element={
        (() => {
          console.log('=== TRAINER PORTAL ACCESS ===');
          console.log('isClientLoggedIn:', isClientLoggedIn);
          console.log('isTrainerLoggedIn:', isTrainerLoggedIn);
            console.log('isTrainerSetup:', isTrainerSetup);
          console.log('localStorage trainerId:', localStorage.getItem('trainerId'));
          
          return (
            <TrainerPortal 
                onBackToHome={handleBackToHome}
              onLogout={handleLogout}
              isLoggedIn={isTrainerLoggedIn}
            />
          );
        })()
        } />
        
        <Route path="/trainer-setup" element={
          <TrainerSetup 
            onBackToHome={handleBackToHome} 
            onSetupComplete={() => {
            setIsTrainerLoggedIn(true);
              navigate('/trainer');
            }}
            userData={null}
          />
        } />
        
        <Route path="/video-call" element={<VideoCallPage />} />
        <Route path="/post-session" element={<PostSessionForm />} />
        
        <Route path="/trainer/connect/complete" element={
          <div style={{
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>&#x2705;</div>
              <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '15px', color: '#2c3e50' }}>
                Setup Complete
              </h1>
              <p style={{ fontSize: '1.1rem', marginBottom: '30px', color: '#7f8c8d' }}>
                Your bank account has been connected. You can now receive session earnings.
              </p>
              <button
                onClick={() => navigate('/trainer')}
                style={{
                  padding: '12px 30px',
                  background: '#635bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Back to Trainer Portal
              </button>
            </div>
          </div>
        } />
        <Route path="/trainer/connect/refresh" element={
          <div style={{
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>&#x1F504;</div>
              <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '15px', color: '#2c3e50' }}>
                Session Expired
              </h1>
              <p style={{ fontSize: '1.1rem', marginBottom: '30px', color: '#7f8c8d' }}>
                Your onboarding session has expired. Please try again from your trainer portal.
              </p>
              <button
                onClick={() => navigate('/trainer')}
                style={{
                  padding: '12px 30px',
                  background: '#635bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Back to Trainer Portal
              </button>
            </div>
          </div>
        } />

        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/trainer-setup/payment-success" element={<TrainerPaymentSuccess />} />
        <Route path="/payment/cancel" element={
          <div style={{
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            color: 'white'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              color: '#2c3e50'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>⚠️</div>
              <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '15px', color: '#e74c3c' }}>
                Payment Cancelled
              </h1>
              <p style={{ fontSize: '1.1rem', marginBottom: '30px', color: '#7f8c8d' }}>
                Your payment was cancelled. No charges were made.
              </p>
              <button
                onClick={() => navigate('/client-portal')}
                style={{
                  padding: '12px 30px',
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Return to Portal
              </button>
            </div>
          </div>
        } />
        
      </Routes>
    </div>
  );
}

export default App;
